import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { companyPermissionModules } from "../../../../utils/Constants";
import {
  canViewCompanyModules,
  isSupervisorManger,
} from "../../../../utils/Permissions";
import { getCompanyEventSatisfactionSurvey } from "../../../../network/apis/companyPortalEvaluations";
import { getAdminEventSatisfactionSurvey } from "../../../../network/apis/satisfactionSurvey";
import CompanyEvaluationResult from "./CompanyEvaluationResult";

// Read-only view of the client's satisfaction survey for an event (Company /
// Admin portals). The survey lives inside the Evaluations tab, so viewing
// `Evaluations` is enough; `Survey Score` also grants it.
const EventSatisfactionSurveyView = ({ event, portalVariant }) => {
  const login = useSelector((state) => state.auth.login);
  const isAdmin = portalVariant === "admin-portal";
  // Supervisor sessions use user_app tokens — admin/company survey APIs 401.
  const canView =
    !isSupervisorManger() &&
    (isAdmin ||
      canViewCompanyModules(
        [
          companyPermissionModules.evaluations,
          companyPermissionModules.surveyScore,
        ],
        login,
      ));

  const [survey, setSurvey] = useState(null);

  useEffect(() => {
    if (!event?.id || !canView) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const fetcher = isAdmin
          ? getAdminEventSatisfactionSurvey
          : getCompanyEventSatisfactionSurvey;
        const res = await fetcher(event.id);
        if (!cancelled) setSurvey(res?.data?.data ?? null);
      } catch (e) {
        // 403 (no permission) / 404 (out of scope) — fall back to empty state.
        if (!cancelled) setSurvey(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [event, canView, isAdmin]);

  return <CompanyEvaluationResult survey={survey} />;
};

EventSatisfactionSurveyView.propTypes = {
  event: PropTypes.shape({ id: PropTypes.string }),
  portalVariant: PropTypes.oneOf(["company-portal", "admin-portal"]),
};

EventSatisfactionSurveyView.defaultProps = {
  event: null,
  portalVariant: "company-portal",
};

export default EventSatisfactionSurveyView;
